/**
 * FeatureFlagsSettings — Admin panelinden tüm feature flag'leri aç/kapa.
 *
 * Şub 16, 2026 — GET /api/feature-flags → kategori bazlı gruplanmış liste.
 * Her satırda toggle switch; toggle → PUT /api/feature-flags/{key}. Değişiklik
 * anında context'e refresh gönderir → tüm uygulamada gate durumları güncellenir.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Flag, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../lib/api';
import { useFeatureFlags } from '../../lib/useFeatureFlag';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const CATEGORY_META = {
  employee_portal: { label: 'Çalışan Portalı', color: 'blue' },
  planning:        { label: 'Planlama',         color: 'emerald' },
  postnl:          { label: 'PostNL',           color: 'orange' },
  ui:              { label: 'Arayüz',           color: 'violet' },
  general:         { label: 'Genel',            color: 'slate' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR'); } catch { return iso; }
}

export default function FeatureFlagsSettings() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const { refresh: refreshCtx } = useFeatureFlags();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/feature-flags');
      setFlags(res.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Flag listesi alınamadı');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (flag) => {
    setPending(flag.key);
    try {
      const res = await api.put(`/feature-flags/${flag.key}`, { enabled: !flag.enabled });
      const updated = res.data?.data;
      setFlags((cur) => cur.map((f) => (f.key === flag.key ? updated : f)));
      await refreshCtx();
      toast.success(`${updated.name}: ${updated.enabled ? 'AÇIK' : 'KAPALI'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle başarısız');
    } finally { setPending(null); }
  };

  const grouped = useMemo(() => {
    const map = {};
    for (const f of flags) {
      const cat = f.category || 'general';
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    }
    return map;
  }, [flags]);

  return (
    <div className="space-y-4" data-testid="feature-flags-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Flag className="h-6 w-6 text-orange-500" />
            Feature Flags
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Uygulama özelliklerini tek tıkla aç/kapa. Değişiklikler tüm oturumlara anında yansır.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ff-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {loading && flags.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" /> <span className="ml-2">Yükleniyor…</span>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
          <Info className="h-8 w-8 opacity-40" />
          Hiç feature flag tanımlı değil.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.general;
          return (
            <Card key={cat} data-testid={`ff-category-${cat}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full bg-${meta.color}-500`} />
                  {meta.label}
                  <Badge variant="outline" className="text-[10px] ml-2">{list.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {list.map((f) => (
                  <div
                    key={f.key}
                    className={`flex items-start justify-between gap-4 rounded border px-3 py-3 transition ${
                      f.enabled
                        ? 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
                    }`}
                    data-testid={`ff-row-${f.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{f.name}</span>
                        <code className="text-[10px] bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 font-mono text-slate-500">{f.key}</code>
                        {f.enabled ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]">AÇIK</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-slate-500">KAPALI</Badge>
                        )}
                      </div>
                      {f.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{f.description}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">
                        Son değişiklik: {fmtDate(f.updated_at)} · {f.updated_by_name || '—'}
                      </p>
                    </div>
                    <div className="pt-1 flex-shrink-0">
                      {pending === f.key ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      ) : (
                        <Switch
                          checked={!!f.enabled}
                          onCheckedChange={() => toggle(f)}
                          data-testid={`ff-toggle-${f.key}`}
                          aria-label={`Toggle ${f.name}`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
